import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Cookie, Copy, Save, Eye, Plus, Trash2, ArrowLeft, Globe, X } from 'lucide-react';
import api, { API } from '../lib/api';
import { formatDateTime } from '../lib/constants';
import MissionShell from '../components/MissionShell';
import { MISSIONS } from '../lib/missions';
import QuotaBanner from '../components/QuotaBanner';
import UpgradeRequestModal from '../components/UpgradeRequestModal';
import { useQuotaUsage } from '../lib/useQuotaUsage';

const CAT_LABELS = {
  necesarias: 'Estrictamente necesarias',
  funcionales: 'Funcionales',
  analiticas: 'Analíticas',
  publicitarias: 'Publicitarias / Marketing',
};

export default function Cmp() {
  const [tab, setTab] = useState('banners');
  const [stats, setStats] = useState(null);
  useEffect(() => {
    Promise.all([api.get('/cmp/consents?limit=200'), api.get('/cmp/configs'), api.get('/documents')])
      .then(([c, cfgs, d]) => setStats({
        consents: c.data.total || 0,
        lastConsent: c.data.items?.[0]?.created_at,
        banners: (cfgs.data.configs || []).length,
        hasCookieDoc: d.data.some((x) => x.doc_type === 'cookies_notice'),
      }))
      .catch(() => setStats({ consents: 0, banners: 1, hasCookieDoc: true }));
  }, []);

  return (
    <MissionShell
      mission={MISSIONS.cookies}
      idPrefix="cmp-mission"
      prereq={stats && !stats.hasCookieDoc ? { message: 'Tu banner debe enlazar a un aviso de cookies. Créalo primero en Documentos legales.', to: '/app/documentos', cta: 'Ir a Documentos legales' } : null}
      doHint={stats?.consents
        ? 'Tus banners están recolectando consentimientos. Revisa la configuración cuando agregues nuevas herramientas de analítica o publicidad.'
        : 'Siguiente acción: configura el banner de tu sitio, copia su snippet e instálalo antes de </head> para empezar a registrar consentimientos.'}
      evidence={[
        { label: 'Banners configurados', value: stats?.banners ?? '—', tone: 'ok', detail: 'Uno por sitio o página de tu empresa' },
        { label: 'Consentimientos registrados', value: stats?.consents ?? '—', tone: stats?.consents ? 'ok' : 'warn' },
        { label: 'Último consentimiento', value: stats?.lastConsent ? formatDateTime(stats.lastConsent) : '—', tone: 'ok' },
      ]}
      evidenceNote="Cada consentimiento queda guardado con fecha, sitio, versión del banner y elección del visitante."
    >
      <div className="space-y-6" data-testid="cmp-page">
        <div className="border-b border-[#E5E7EB]">
          <div className="flex gap-1 -mb-px">
            <TabBtn active={tab === 'banners'} onClick={() => setTab('banners')} testid="cmp-tab-banners">Banners y sitios</TabBtn>
            <TabBtn active={tab === 'consents'} onClick={() => setTab('consents')} testid="cmp-tab-consents">Consentimientos</TabBtn>
          </div>
        </div>
        {tab === 'banners' ? <BannersTab /> : <ConsentsTab />}
      </div>
    </MissionShell>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`px-4 py-2.5 text-[13px] border-b-2 transition-colors ${active ? 'border-[#143A34] text-[#1C2329] font-medium' : 'border-transparent text-[#6B7280] hover:text-[#374151]'}`}>
      {children}
    </button>
  );
}

/* ---------- Lista de banners (multi-sitio) ---------- */

function BannersTab() {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [domain, setDomain] = useState('');
  const [showUpgrade, setShowUpgrade] = useState(false);
  const { usage, refresh } = useQuotaUsage();

  const load = async () => {
    const { data } = await api.get('/cmp/configs');
    setData(data);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await api.post('/cmp/configs', { name: name.trim(), domain: domain.trim() || null });
      toast.success('Banner creado');
      setCreating(false); setName(''); setDomain('');
      await load();
      await refresh();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d && typeof d === 'object' && d.error === 'quota_exceeded') {
        toast.error(d.message || 'Alcanzó el tope del plan.');
        setCreating(false);
        setShowUpgrade(true);
      } else {
        toast.error(d || 'Error');
      }
    }
  };

  if (!data) return <div className="text-sm text-[#6B7280]">Cargando banners…</div>;

  if (editing) {
    return (
      <BannerEditor
        cfg={editing}
        categories={data.categories}
        canDelete={data.configs.length > 1}
        onBack={async () => { setEditing(null); await load(); await refresh(); }}
      />
    );
  }

  return (
    <div className="space-y-4" data-testid="cmp-banners">
      <QuotaBanner usage={usage} quotaKey="cmp_domains" onRequestUpgrade={() => setShowUpgrade(true)} />
      <UpgradeRequestModal open={showUpgrade} onClose={(sent) => { setShowUpgrade(false); if (sent) refresh(); }} />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-[13px] text-[#6B7280]">
          {data.configs.length} banner{data.configs.length === 1 ? '' : 's'} · uno por cada sitio o página de tu empresa
        </div>
        <button className="pv-btn-primary flex items-center gap-1.5" onClick={() => setCreating(true)} data-testid="cmp-new-banner">
          <Plus size={14} /> Nuevo banner
        </button>
      </div>

      {creating && (
        <div className="border border-[#143A34]/30 bg-[#F0FDF4] p-4 space-y-3" data-testid="cmp-create-form">
          <div className="flex items-center justify-between">
            <div className="text-[13px] font-medium text-[#1C2329]">Nuevo banner de cookies</div>
            <button onClick={() => setCreating(false)} className="text-[#6B7280] hover:text-[#1C2329]"><X size={14} /></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Nombre del sitio *">
              <input className="pv-input" placeholder="Ej: Tienda online" value={name} onChange={(e) => setName(e.target.value)} data-testid="cmp-new-name" autoFocus />
            </Field>
            <Field label="Dominio (referencia)">
              <input className="pv-input" placeholder="Ej: tienda.miempresa.cl" value={domain} onChange={(e) => setDomain(e.target.value)} data-testid="cmp-new-domain" />
            </Field>
          </div>
          <div className="flex justify-end gap-2">
            <button className="pv-btn-secondary text-[13px]" onClick={() => setCreating(false)}>Cancelar</button>
            <button className="pv-btn-primary" onClick={create} disabled={!name.trim()} data-testid="cmp-new-save">Crear banner</button>
          </div>
        </div>
      )}

      <div className="border border-[#E5E7EB] bg-white overflow-x-auto">
        <table className="pv-table" data-testid="cmp-banners-table">
          <thead><tr>
            <th>Sitio</th>
            <th>Dominio</th>
            <th style={{ width: 90 }}>Versión</th>
            <th style={{ width: 100 }}>Estado</th>
            <th style={{ width: 110 }}></th>
          </tr></thead>
          <tbody>
            {data.configs.map((c) => (
              <tr key={c.id} className="cursor-pointer" onClick={() => setEditing(c)} data-testid={`cmp-banner-${c.id}`}>
                <td>
                  <div className="flex items-center gap-2">
                    <Globe size={13} className="text-[#6B7280]" strokeWidth={1.5} />
                    <span className="font-medium text-[#1C2329]">{c.name || 'Sitio principal'}</span>
                  </div>
                </td>
                <td className="text-[12.5px] text-[#6B7280] font-mono">{c.domain || '—'}</td>
                <td className="text-[12px] font-mono">v{c.banner_version}</td>
                <td>
                  {c.active
                    ? <span className="text-[11px] px-1.5 py-0.5 bg-[#ECFDF5] text-[#065F46]">Activo</span>
                    : <span className="text-[11px] px-1.5 py-0.5 bg-[#F3F4F6] text-[#6B7280]">Inactivo</span>}
                </td>
                <td className="text-right text-[12px] text-[#143A34]">Editar →</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ---------- Editor de un banner ---------- */

function BannerEditor({ cfg: initial, categories, canDelete, onBack }) {
  const [cfg, setCfg] = useState(initial);
  const [saving, setSaving] = useState(false);

  const snippet = `<script async src="${API}/public/cmp/${cfg.public_slug}.js"></script>`;

  const setField = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  const setCategory = (k, patch) => setCfg((c) => ({
    ...c, categories: { ...c.categories, [k]: { ...c.categories[k], ...patch } },
  }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: cfg.name, domain: cfg.domain || null,
        active: cfg.active, title: cfg.title, body: cfg.body,
        accept_all_label: cfg.accept_all_label, reject_all_label: cfg.reject_all_label,
        customize_label: cfg.customize_label, policy_url: cfg.policy_url || null,
        brand_primary: cfg.brand_primary, position: cfg.position, categories: cfg.categories,
      };
      const { data } = await api.put(`/cmp/configs/${cfg.id}`, payload);
      toast.success(`Banner guardado · v${data.banner_version}`);
      setCfg(data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };

  const del = async () => {
    if (!window.confirm('¿Eliminar este banner? Los consentimientos ya registrados se conservan.')) return;
    try {
      await api.delete(`/cmp/configs/${cfg.id}`);
      toast.success('Banner eliminado');
      onBack();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
  };

  return (
    <div className="space-y-4" data-testid="cmp-editor">
      <div className="flex items-center justify-between gap-3">
        <button onClick={onBack} className="text-[13px] text-[#6B7280] hover:text-[#1C2329] inline-flex items-center gap-1.5" data-testid="cmp-editor-back">
          <ArrowLeft size={13} /> Volver a banners
        </button>
        {canDelete && (
          <button onClick={del} className="text-[12.5px] text-[#DC2626] hover:bg-[#FEF2F2] px-2.5 py-1.5 inline-flex items-center gap-1.5" data-testid="cmp-editor-delete">
            <Trash2 size={13} /> Eliminar banner
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6">
        <div className="space-y-5">
          <SnippetBox snippet={snippet} />

          <div className="grid grid-cols-2 gap-4">
            <Field label="Nombre del sitio">
              <input className="pv-input" value={cfg.name || ''} onChange={(e) => setField('name', e.target.value)} data-testid="cmp-name" />
            </Field>
            <Field label="Dominio (referencia)">
              <input className="pv-input" placeholder="Ej: tienda.miempresa.cl" value={cfg.domain || ''} onChange={(e) => setField('domain', e.target.value)} data-testid="cmp-domain" />
            </Field>
          </div>

          <Field label="Estado del banner">
            <label className="inline-flex items-center gap-2 text-[13px]">
              <input type="checkbox" className="w-4 h-4 accent-[#143A34]"
                checked={cfg.active} onChange={(e) => setField('active', e.target.checked)} data-testid="cmp-active" />
              Banner activo (se muestra a nuevos visitantes)
            </label>
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Color primario">
              <div className="flex items-center gap-2">
                <input type="color" value={cfg.brand_primary}
                  onChange={(e) => setField('brand_primary', e.target.value)}
                  className="w-10 h-9 border border-[#E5E7EB] rounded-sm cursor-pointer" data-testid="cmp-color" />
                <input className="pv-input font-mono text-[12px]" value={cfg.brand_primary}
                  onChange={(e) => setField('brand_primary', e.target.value)} />
              </div>
            </Field>
            <Field label="URL de la política de cookies">
              <input className="pv-input" placeholder="https://miempresa.cl/cookies"
                value={cfg.policy_url || ''} onChange={(e) => setField('policy_url', e.target.value)} />
            </Field>
          </div>

          <Field label="Título del banner">
            <input className="pv-input" value={cfg.title} onChange={(e) => setField('title', e.target.value)} data-testid="cmp-title" />
          </Field>
          <Field label="Cuerpo del banner">
            <textarea className="pv-input" rows={3} value={cfg.body} onChange={(e) => setField('body', e.target.value)} data-testid="cmp-body" />
          </Field>

          <div className="grid grid-cols-3 gap-3">
            <Field label='Texto "aceptar"'>
              <input className="pv-input" value={cfg.accept_all_label} onChange={(e) => setField('accept_all_label', e.target.value)} />
            </Field>
            <Field label='Texto "rechazar"'>
              <input className="pv-input" value={cfg.reject_all_label} onChange={(e) => setField('reject_all_label', e.target.value)} />
            </Field>
            <Field label='Texto "personalizar"'>
              <input className="pv-input" value={cfg.customize_label} onChange={(e) => setField('customize_label', e.target.value)} />
            </Field>
          </div>

          <div>
            <div className="text-[12px] font-medium text-[#374151] mb-2">Categorías de cookies</div>
            <div className="space-y-2">
              {Object.entries(categories).map(([k, label]) => {
                const c = cfg.categories[k] || { enabled: false, required: false, desc: '' };
                return (
                  <div key={k} className="border border-[#E5E7EB] p-3">
                    <div className="flex flex-wrap items-center gap-3 mb-2">
                      <label className="inline-flex items-center gap-1.5 text-[13px] font-medium text-[#1C2329]">
                        <input type="checkbox" className="w-4 h-4 accent-[#143A34]"
                          checked={c.enabled} onChange={(e) => setCategory(k, { enabled: e.target.checked })}
                          data-testid={`cmp-cat-${k}-enabled`} />
                        {label}
                      </label>
                      <label className="inline-flex items-center gap-1.5 text-[11.5px] text-[#6B7280]">
                        <input type="checkbox" className="w-3.5 h-3.5 accent-[#143A34]"
                          checked={!!c.required} onChange={(e) => setCategory(k, { required: e.target.checked })} />
                        Siempre activa (obligatoria)
                      </label>
                    </div>
                    <input className="pv-input text-[12.5px]" placeholder="Descripción visible al visitante"
                      value={c.desc || ''} onChange={(e) => setCategory(k, { desc: e.target.value })} />
                  </div>
                );
              })}
            </div>
          </div>

          <div className="flex items-center justify-between border-t border-[#E5E7EB] pt-4">
            <div className="text-[11.5px] text-[#6B7280]">
              Versión actual del banner: <b>v{cfg.banner_version}</b>. Cada cambio significativo genera una nueva versión.
            </div>
            <button className="pv-btn-primary flex items-center gap-1.5" onClick={save} disabled={saving} data-testid="cmp-save">
              <Save size={13} /> {saving ? 'Guardando…' : 'Guardar banner'}
            </button>
          </div>
        </div>

        <div>
          <div className="text-[11px] uppercase tracking-wider text-[#6B7280] font-medium mb-2 flex items-center gap-1.5">
            <Eye size={12} /> Vista previa
          </div>
          <div className="border border-[#E5E7EB] bg-[#FAFBFC] p-4 sticky top-4">
            <div className="text-[11px] text-[#6B7280] mb-3">Así verá el banner un visitante nuevo de su sitio:</div>
            <BannerPreview cfg={cfg} />
          </div>
        </div>
      </div>
    </div>
  );
}

function SnippetBox({ snippet }) {
  return (
    <div className="bg-[#0B1220] text-white p-4" data-testid="cmp-snippet">
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="text-[11px] text-white/60 uppercase tracking-wider flex items-center gap-1.5">
          <Cookie size={11} /> Snippet embebible · pegue esto en su sitio antes de <code className="text-white/80">&lt;/head&gt;</code>
        </div>
        <button className="text-[11px] text-white/80 hover:text-white flex items-center gap-1"
          onClick={() => { navigator.clipboard.writeText(snippet); toast.success('Snippet copiado'); }}
          data-testid="cmp-copy">
          <Copy size={11} /> Copiar
        </button>
      </div>
      <pre className="font-mono text-[11.5px] overflow-x-auto text-[#7EE0AC]">{snippet}</pre>
    </div>
  );
}

function BannerPreview({ cfg }) {
  return (
    <div className="bg-white border border-[#E5E7EB] shadow-sm" style={{ fontFamily: 'inherit' }}>
      <div className="px-4 pt-3 text-[13px] font-semibold text-[#1C2329]">{cfg.title}</div>
      <div className="px-4 pt-1 pb-2 text-[11.5px] text-[#374151] leading-relaxed">{cfg.body}</div>
      {cfg.policy_url && (
        <div className="px-4 pb-2">
          <a href={cfg.policy_url} target="_blank" rel="noopener noreferrer" className="text-[11px] underline" style={{ color: cfg.brand_primary }}>Ver política de cookies</a>
        </div>
      )}
      <div className="px-4 py-3 border-t border-[#F3F4F6] flex flex-wrap gap-2 justify-end">
        <button className="text-[11.5px] px-2.5 py-1 border border-[#E5E7EB] bg-white">{cfg.customize_label}</button>
        <button className="text-[11.5px] px-2.5 py-1 border border-[#E5E7EB] bg-white">{cfg.reject_all_label}</button>
        <button className="text-[11.5px] px-2.5 py-1 text-white" style={{ background: cfg.brand_primary, borderColor: cfg.brand_primary }}>{cfg.accept_all_label}</button>
      </div>
      <div className="px-4 py-1.5 text-center text-[9.5px] text-[#9CA3AF] border-t border-[#F3F4F6]">Gestionado con Priviax · Ley N.º 21.719</div>
    </div>
  );
}

/* ---------- Consentimientos (con filtro por sitio) ---------- */

function ConsentsTab() {
  const [data, setData] = useState(null);
  const [sites, setSites] = useState([]);
  const [siteFilter, setSiteFilter] = useState('');

  const load = async (configId = '') => {
    const qs = configId ? `&config_id=${configId}` : '';
    const { data } = await api.get(`/cmp/consents?limit=200${qs}`);
    setData(data);
  };
  useEffect(() => {
    load();
    api.get('/cmp/configs').then(({ data }) => setSites(data.configs || [])).catch(() => {});
  }, []);

  const onFilter = (v) => { setSiteFilter(v); load(v); };

  if (!data) return <div className="text-sm text-[#6B7280]">Cargando consentimientos…</div>;
  if (data.total === 0 && !siteFilter) {
    return (
      <div className="border border-[#E5E7EB] bg-white p-10 text-center" data-testid="cmp-consents-empty">
        <Cookie size={26} className="mx-auto text-[#9CA3AF]" strokeWidth={1.5} />
        <div className="mt-3 text-lg font-semibold text-[#1C2329]">Sin consentimientos registrados aún</div>
        <div className="text-sm text-[#6B7280] mt-1">Instale el snippet en su sitio para empezar a recolectar evidencia auditable.</div>
      </div>
    );
  }
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-[13px] text-[#6B7280]">{data.total} consentimientos registrados (últimos {data.items.length})</div>
        {sites.length > 1 && (
          <select className="pv-input !w-auto text-[12.5px]" value={siteFilter} onChange={(e) => onFilter(e.target.value)} data-testid="cmp-site-filter">
            <option value="">Todos los sitios</option>
            {sites.map((s) => <option key={s.id} value={s.id}>{s.name || 'Sitio principal'}</option>)}
          </select>
        )}
      </div>
      <div className="border border-[#E5E7EB] bg-white overflow-x-auto">
        <table className="pv-table" data-testid="cmp-consents-table">
          <thead><tr>
            <th style={{ width: 140 }}>Fecha</th>
            {sites.length > 1 && <th style={{ width: 130 }}>Sitio</th>}
            <th style={{ width: 90 }}>Acción</th>
            <th>Categorías aceptadas</th>
            <th style={{ width: 60 }}>Banner v</th>
            <th style={{ width: 130 }}>IP (hash)</th>
            <th>User-Agent</th>
          </tr></thead>
          <tbody>
            {data.items.map((c) => (
              <tr key={c.id} data-testid={`cmp-consent-${c.id}`}>
                <td className="text-[11.5px] font-mono">{formatDateTime(c.created_at)}</td>
                {sites.length > 1 && <td className="text-[12px]">{c.site_name || 'Sitio principal'}</td>}
                <td className="text-[12px]"><ActionBadge action={c.action} /></td>
                <td className="text-[12px]">
                  {Object.entries(c.accepted || {}).filter(([, v]) => v).map(([k]) => CAT_LABELS[k] || k).join(', ') || '—'}
                </td>
                <td className="text-[11.5px] font-mono text-center">v{c.banner_version}</td>
                <td className="text-[10.5px] font-mono text-[#6B7280]">{c.ip_hash || '—'}</td>
                <td className="text-[10.5px] text-[#6B7280] truncate max-w-[240px]" title={c.user_agent}>{c.user_agent || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ActionBadge({ action }) {
  const map = {
    accept_all:      { bg: '#ECFDF5', color: '#065F46', label: 'Aceptó todo' },
    reject_optional: { bg: '#F3F4F6', color: '#374151', label: 'Rechazó opcionales' },
    custom:          { bg: '#EFF6FF', color: '#1E40AF', label: 'Personalizó' },
  };
  const m = map[action] || { bg: '#F3F4F6', color: '#6B7280', label: action };
  return <span className="text-[10.5px] px-1.5 py-0.5" style={{ background: m.bg, color: m.color }}>{m.label}</span>;
}

function Field({ label, children }) {
  return <div><label className="block text-[12px] font-medium text-[#374151] mb-1.5">{label}</label>{children}</div>;
}
